"use client";

import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
  Text,
} from "@chakra-ui/react";
import {
  phoneNumberService,
  type ImportPhoneNumberParams,
} from "../../services/phoneNumberService.ts";
import { useAppTheme } from "../../ThemeContext.tsx";

interface ImportPhoneNumberDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function ImportPhoneNumberDialog({
  isOpen,
  onClose,
  onSuccess,
}: ImportPhoneNumberDialogProps) {
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [formData, setFormData] = React.useState<ImportPhoneNumberParams>({
    phone_number: "",
    termination_uri: "",
    sip_trunk_auth_username: "",
    sip_trunk_auth_password: "",
    nickname: "",
    inbound_agent_id: null,
    outbound_agent_id: null,
    inbound_webhook_url: null,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.phone_number || !formData.termination_uri) {
      toast({
        title: "Required fields missing",
        description: "Please fill in all required fields",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await phoneNumberService.importPhoneNumber(formData);
      toast({
        title: "Phone number imported",
        description: "Your phone number has been successfully imported.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error importing phone number:", error);
      toast({
        title: "Error importing phone number",
        description:
          error.response?.data?.message || "An unexpected error occurred",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent bg={isDark ? "gray.800" : "white"}>
        <ModalHeader color={isDark ? "white" : "gray.800"}>
          Connect to your number via SIP trunking
        </ModalHeader>
        <ModalCloseButton color={isDark ? "white" : "gray.800"} />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel color={isDark ? "white" : "gray.800"}>
                Phone Number
              </FormLabel>
              <Input
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                placeholder="Enter phone number"
                bg={isDark ? "gray.700" : "white"}
                color={isDark ? "white" : "gray.800"}
                borderColor={isDark ? "gray.600" : "gray.200"}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel color={isDark ? "white" : "gray.800"}>
                Termination URI
              </FormLabel>
              <Input
                name="termination_uri"
                value={formData.termination_uri}
                onChange={handleChange}
                placeholder="Enter termination URI"
                bg={isDark ? "gray.700" : "white"}
                color={isDark ? "white" : "gray.800"}
                borderColor={isDark ? "gray.600" : "gray.200"}
              />
            </FormControl>

            <FormControl>
              <FormLabel color={isDark ? "white" : "gray.800"}>
                SIP Trunk User Name{" "}
                <Text as="span" color="gray.500">
                  (Optional)
                </Text>
              </FormLabel>
              <Input
                id="sip_trunk_auth_username"
                name="sip_trunk_auth_username"
                value={formData.sip_trunk_auth_username}
                onChange={handleChange}
                placeholder="Enter SIP Trunk User Name"
                bg={isDark ? "gray.700" : "white"}
                color={isDark ? "white" : "gray.800"}
                borderColor={isDark ? "gray.600" : "gray.200"}
              />
            </FormControl>

            <FormControl>
              <FormLabel color={isDark ? "white" : "gray.800"}>
                SIP Trunk Password{" "}
                <Text as="span" color="gray.500">
                  (Optional)
                </Text>
              </FormLabel>
              <Input
                id="sip_trunk_auth_password"
                name="sip_trunk_auth_password"
                value={formData.sip_trunk_auth_password}
                onChange={handleChange}
                placeholder="Enter SIP Trunk Password"
                type="password"
                bg={isDark ? "gray.700" : "white"}
                color={isDark ? "white" : "gray.800"}
                borderColor={isDark ? "gray.600" : "gray.200"}
              />
            </FormControl>

            <FormControl>
              <FormLabel color={isDark ? "white" : "gray.800"}>
                Nickname{" "}
                <Text as="span" color="gray.500">
                  (Optional)
                </Text>
              </FormLabel>
              <Input
                name="nickname"
                value={formData.nickname}
                onChange={handleChange}
                placeholder="Enter Nickname"
                bg={isDark ? "gray.700" : "white"}
                color={isDark ? "white" : "gray.800"}
                borderColor={isDark ? "gray.600" : "gray.200"}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme={isDark ? "black" : "white"}
            textColor={isDark ? "whiteAlpha" : "gray.800"}
            variant="ghost"
            mr={3}
            onClick={onClose}
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            colorScheme="gray"
            _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
            background={isDark ? "gray.700" : "gray.200"}
            textColor={isDark ? "whiteAlpha" : "gray.800"}
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText="Saving..."
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
